import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  UsersEndpoints as Endpoints,
  type AddUserSocialIntegrationsRequest,
  type AddUserSocialIntegrationsResponse,
  type GetEmployeesListRequest,
  type GetEmployeesListResponse,
  type GetOrganizationUsersRequest,
  type GetOrganizationUsersResponse,
  type GetUserSocialIntegrationsForAdditionRequest,
  type GetUserSocialIntegrationsForAdditionResponse,
  type GetBranchEmployeesRequest,
  type GetBranchEmployeesResponse,
  type GetEmployeesSearchResultRequest,
  type GetEmployeesSearchResultResponse,
  type ExportEmployeesToExcelRequest,
  type ExportEmployeesToExcelByIdsRequest,
  type ExportEmployeesToExcelByIdsResponse,
  type ExportEmployeesToExcelResponse,
  type GetBranchEmployeesForSystemRequest,
  type GetBranchEmployeesForSystemResponse,
  type UsersGetClientsFilterDataRequest,
  type UsersGetClientsFilterDataResponse,
  type GetUserHistoryFilterDataRequest,
  type GetUserHistoryFilterDataResponse,
  type GetUserHistorySearchResultRequest,
  type GetUserHistorySearchResultResponse,
  type GetUsersByIdsRequest,
  type GetUsersByIdsResponse,
  type GetUsersFilterDataRequest,
  type GetUsersFilterDataResponse,
  type ImportExcelRequest,
  type ImportExcelResponse,
  type GetEmployeesWithoutCurrentBranchRequest,
  type GetEmployeesWithoutCurrentBranchResponse,
  type GetUsersSearchResultRequest,
  type GetUsersSearchResultResponse,
  type GetUserSocialIntegrationsRequest,
  type GetUserSocialIntegrationsResponse,
  type RemoveUserSocialIntegrationsRequest,
  type RemoveUserSocialIntegrationsResponse,
  type SearchEmployeesRequest,
  type SearchEmployeesResponse,
  type SearchUserHistoryRequest,
  type SearchUserHistoryResponse,
  type SearchUsersRequest,
  type SearchUsersResponse,
  type StopImportExcelRequest,
  type StopImportExcelResponse,
} from '@app/api/Users/types';
import { USERS_CHUNK } from '@app/constants';

export const usersApi2 = createApi({
  reducerPath: 'usersApi2',
  baseQuery: createApiBaseQuery(USERS_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetUserSocialIntegrations]: query<
      GetUserSocialIntegrationsResponse,
      GetUserSocialIntegrationsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserSocialIntegrations,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.AddUserSocialIntegrations]: mutation<
      AddUserSocialIntegrationsResponse,
      AddUserSocialIntegrationsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddUserSocialIntegrations,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.RemoveUserSocialIntegrations]: mutation<
      RemoveUserSocialIntegrationsResponse,
      RemoveUserSocialIntegrationsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.RemoveUserSocialIntegrations,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetUserSocialIntegrationsForAddition]: query<
      GetUserSocialIntegrationsForAdditionResponse,
      GetUserSocialIntegrationsForAdditionRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserSocialIntegrationsForAddition,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetOrganizationUsers]: query<
      GetOrganizationUsersResponse,
      GetOrganizationUsersRequest
    >({
      query: (params) => ({
        url: Endpoints.GetOrganizationUsers,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeesList]: query<
      GetEmployeesListResponse,
      GetEmployeesListRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeesList,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchUsers]: mutation<SearchUsersResponse, SearchUsersRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchUsers,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetUsersSearchResult]: query<
      GetUsersSearchResultResponse,
      GetUsersSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUsersSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchEmployees]: mutation<
      SearchEmployeesResponse,
      SearchEmployeesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchEmployees,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetEmployeesSearchResult]: query<
      GetEmployeesSearchResultResponse,
      GetEmployeesSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeesSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchEmployees]: query<
      GetBranchEmployeesResponse,
      GetBranchEmployeesRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetBranchEmployees,
        method: Methods.GET,
        params,
        logs,
      }),
    }),

    [Endpoints.ExportEmployeesToExcel]: query<
      ExportEmployeesToExcelResponse,
      ExportEmployeesToExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportEmployeesToExcel,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportEmployeesToExcelByIds]: mutation<
      ExportEmployeesToExcelByIdsResponse,
      ExportEmployeesToExcelByIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportEmployeesToExcelByIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ImportExcel]: mutation<ImportExcelResponse, ImportExcelRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.ImportExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.StopImportExcel]: mutation<
      StopImportExcelResponse,
      StopImportExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.StopImportExcel,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.GetUserHistoryFilterData]: query<
      GetUserHistoryFilterDataResponse,
      GetUserHistoryFilterDataRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetUserHistoryFilterData,
        method: Methods.GET,
        params,
        logs,
      }),
    }),
    [Endpoints.SearchUserHistory]: mutation<
      SearchUserHistoryResponse,
      SearchUserHistoryRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchUserHistory,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetUserHistorySearchResult]: query<
      GetUserHistorySearchResultResponse,
      GetUserHistorySearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserHistorySearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetUsersFilterData]: query<
      GetUsersFilterDataResponse,
      GetUsersFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUsersFilterData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetUsersByIds]: mutation<
      GetUsersByIdsResponse,
      GetUsersByIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetUsersByIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetEmployeesWithoutCurrentBranch]: query<
      GetEmployeesWithoutCurrentBranchResponse,
      GetEmployeesWithoutCurrentBranchRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeesWithoutCurrentBranch,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetBranchEmployeesForSystem]: query<
      GetBranchEmployeesForSystemResponse,
      GetBranchEmployeesForSystemRequest
    >({
      query: (params) => ({
        url: Endpoints.GetBranchEmployeesForSystem,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientsFilterData]: mutation<
      UsersGetClientsFilterDataResponse,
      UsersGetClientsFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientsFilterData,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useGetUserSocialIntegrationsQuery,
  useLazyGetUserSocialIntegrationsQuery,
  useAddUserSocialIntegrationsMutation,
  useRemoveUserSocialIntegrationsMutation,
  useGetUserSocialIntegrationsForAdditionQuery,
  useLazyGetUserSocialIntegrationsForAdditionQuery,
  useGetOrganizationUsersQuery,
  useLazyGetOrganizationUsersQuery,
  useGetEmployeesListQuery,
  useLazyGetEmployeesListQuery,
  useSearchUsersMutation,
  useGetUsersSearchResultQuery,
  useLazyGetUsersSearchResultQuery,
  useSearchEmployeesMutation,
  useGetEmployeesSearchResultQuery,
  useLazyGetEmployeesSearchResultQuery,
  useGetBranchEmployeesQuery,
  useLazyGetBranchEmployeesQuery,
  useExportEmployeesToExcelQuery,
  useLazyExportEmployeesToExcelQuery,
  useExportEmployeesToExcelByIdsMutation,
  useImportExcelMutation,
  useStopImportExcelMutation,
  useGetUserHistoryFilterDataQuery,
  useLazyGetUserHistoryFilterDataQuery,
  useSearchUserHistoryMutation,
  useGetUserHistorySearchResultQuery,
  useLazyGetUserHistorySearchResultQuery,
  useGetUsersFilterDataQuery,
  useLazyGetUsersFilterDataQuery,
  useGetUsersByIdsMutation,
  useGetEmployeesWithoutCurrentBranchQuery,
  useLazyGetEmployeesWithoutCurrentBranchQuery,
  useGetBranchEmployeesForSystemQuery,
  useLazyGetBranchEmployeesForSystemQuery,
  useGetClientsFilterDataMutation: useGetUsersClientsFilterDataMutation,
} = usersApi2;
